import { ShippingMethodOption, PaymentGateway, PaymentDetails, PackageItem, CurrencyRatesConfig } from '../types';
import { getCountryInfo } from './countries';
import { sanitizePaymentUrl } from './mobilePaymentUtils';
import { getFedexRate } from './fedexRates';

/**
 * Helper to check if a package has free international shipping
 */
export function isPackageFreeShipping(p: { freeShipping?: boolean; description?: string }): boolean {
  if (p.freeShipping === true) return true;
  const desc = p.description?.toUpperCase() || '';
  return desc.includes('FRETE GRÁTIS') || desc.includes('FRETE GRATIS');
}

export const CURRENCY_RATES_STORAGE_KEY = 'dojapao_currency_rates_config_v2';

export const DEFAULT_CURRENCY_CONFIG: CurrencyRatesConfig = {
  mode: 'automatic',
  spreadPercent: 5.0, // +5% de spread sobre a cotação oficial comercial
  marketJpyToBrl: 0.0355,
  marketUsdToBrl: 5.65,
  marketJpyToUsd: 0.0065,
  manualJpyToBrl: 0.038,
  manualUsdToBrl: 5.80,
  manualJpyToUsd: 0.0065,
  internationalFeePercent: 0.07, // 7% taxa de transferência internacional
  lastUpdated: new Date().toISOString(),
  source: 'Automática (AwesomeAPI / Câmbio Comercial) + Spread 5%'
};

/**
 * Loads the saved currency rate configuration from localStorage or default
 */
export function getSavedCurrencyRatesConfig(): CurrencyRatesConfig {
  try {
    const raw = localStorage.getItem(CURRENCY_RATES_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        ...DEFAULT_CURRENCY_CONFIG,
        ...parsed
      };
    }
  } catch (err) {
    console.warn('Erro ao carregar configuração de cotação:', err);
  }
  return DEFAULT_CURRENCY_CONFIG;
}

/**
 * Persists the currency rate configuration and broadcasts the update event
 */
export function saveCurrencyRatesConfig(config: CurrencyRatesConfig): void {
  try {
    localStorage.setItem(CURRENCY_RATES_STORAGE_KEY, JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('currencyRatesUpdated', { detail: config }));
  } catch (err) {
    console.warn('Erro ao salvar configuração de cotação:', err);
  }
}

/**
 * Calculates the current effective rates applied across the app
 */
export function getEffectiveCurrencyRates(config?: CurrencyRatesConfig) {
  const currentConfig = config || getSavedCurrencyRatesConfig();

  let jpyToBrl: number;
  let usdToBrl: number;
  let jpyToUsd: number;

  if (currentConfig.mode === 'manual') {
    jpyToBrl = currentConfig.manualJpyToBrl || DEFAULT_CURRENCY_CONFIG.manualJpyToBrl;
    usdToBrl = currentConfig.manualUsdToBrl || DEFAULT_CURRENCY_CONFIG.manualUsdToBrl;
    jpyToUsd = currentConfig.manualJpyToUsd || DEFAULT_CURRENCY_CONFIG.manualJpyToUsd;
  } else {
    // Mode is automatic: apply percentage spread on top of market rate
    const spreadMultiplier = 1 + (Number(currentConfig.spreadPercent || 0) / 100);
    const baseJpyToBrl = currentConfig.marketJpyToBrl || DEFAULT_CURRENCY_CONFIG.marketJpyToBrl;
    const baseUsdToBrl = currentConfig.marketUsdToBrl || DEFAULT_CURRENCY_CONFIG.marketUsdToBrl;

    jpyToBrl = parseFloat((baseJpyToBrl * spreadMultiplier).toFixed(5));
    usdToBrl = parseFloat((baseUsdToBrl * spreadMultiplier).toFixed(4));
    jpyToUsd = currentConfig.marketJpyToUsd || DEFAULT_CURRENCY_CONFIG.marketJpyToUsd;
  }

  const internationalFeePercent = currentConfig.internationalFeePercent ?? 0.07;

  return {
    jpyToBrl,
    usdToBrl,
    jpyToUsd,
    internationalFeePercent,
    mode: currentConfig.mode,
    spreadPercent: currentConfig.spreadPercent,
    config: currentConfig
  };
}

/**
 * Fetches real-time live commercial exchange rates from public API (AwesomeAPI)
 */
export async function fetchLiveMarketRates(): Promise<{
  jpyToBrl: number;
  usdToBrl: number;
  jpyToUsd: number;
  source: string;
}> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const response = await fetch(
      'https://economia.awesomeapi.com.br/last/JPY-BRL,USD-BRL,USD-JPY',
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const jpyBrl = data.JPYBRL?.bid ? parseFloat(data.JPYBRL.bid) : 0.0355;
      const usdBrl = data.USDBRL?.bid ? parseFloat(data.USDBRL.bid) : 5.65;
      const usdJpy = data.USDJPY?.bid ? parseFloat(data.USDJPY.bid) : 155.0;
      const jpyToUsd = usdJpy > 0 ? parseFloat((1 / usdJpy).toFixed(6)) : 0.0065;

      return {
        jpyToBrl: jpyBrl,
        usdToBrl: usdBrl,
        jpyToUsd: jpyToUsd,
        source: 'AwesomeAPI (Mercado Financeiro em Tempo Real)'
      };
    }
  } catch (err) {
    console.warn('Cotação ao vivo offline, utilizando valores de referência:', err);
  }

  return {
    jpyToBrl: 0.0355,
    usdToBrl: 5.65,
    jpyToUsd: 0.0065,
    source: 'Referência Padrão (Offline)'
  };
}

/**
 * Dynamic getter proxy for DEFAULT_CURRENCY_RATES for full backward compatibility
 */
export const DEFAULT_CURRENCY_RATES = {
  get jpyToBrl() {
    return getEffectiveCurrencyRates().jpyToBrl;
  },
  get usdToBrl() {
    return getEffectiveCurrencyRates().usdToBrl;
  },
  get jpyToUsd() {
    return getEffectiveCurrencyRates().jpyToUsd;
  },
  get internationalFeePercent() {
    return getEffectiveCurrencyRates().internationalFeePercent;
  }
};

export const SHIPPING_METHODS: ShippingMethodOption[] = [
  {
    id: 'ems',
    type: 'ems',
    name: 'Japan Post - EMS (Express Aéreo)',
    estimatedDays: '5 - 10 dias úteis',
    basePriceYen: 3900,
    pricePerKgYen: 1800,
    maxWeightKg: 30.0,
    description: 'Envio prioritário oficial dos Correios do Japão com código de rastreamento completo até sua porta no Brasil (+25% taxa depósito).'
  },
  {
    id: 'small_packet',
    type: 'small_packet',
    name: 'Japan Post - Small Packet (Aéreo Leve)',
    estimatedDays: '10 - 20 dias úteis',
    basePriceYen: 1800,
    pricePerKgYen: 1200,
    maxWeightKg: 2.0,
    description: 'Opção super econômica para encomendas pequenas e leves de até 2,0 kg com rastreamento (+25% taxa depósito).'
  },
  {
    id: 'sal',
    type: 'sal',
    name: 'Japan Post - SAL (Aéreo Econômico)',
    estimatedDays: '15 - 25 dias úteis',
    basePriceYen: 2700,
    pricePerKgYen: 1100,
    maxWeightKg: 30.0,
    description: 'Aproveita o espaço disponível em voos comerciais (+25% taxa depósito).',
    isTemporarilyUnavailable: true,
    unavailableReason: 'Indisponível no momento para envios ao Brasil (Suspenso pelo Japan Post).'
  },
  {
    id: 'surface',
    type: 'surface',
    name: 'Japan Post - Surface Mail (Navio Marítimo)',
    estimatedDays: '60 - 90 dias',
    basePriceYen: 2200,
    pricePerKgYen: 650,
    maxWeightKg: 30.0,
    description: 'Mais econômico para caixas volumosas e pesadas por via marítima (+25% taxa depósito).',
    isTemporarilyUnavailable: true,
    unavailableReason: 'Indisponível no momento para envios ao Brasil (Suspenso pelo Japan Post).'
  },
  {
    id: 'fedex_priority',
    type: 'fedex',
    name: 'FedEx International Priority',
    estimatedDays: '2 - 4 dias úteis',
    basePriceYen: 0,
    pricePerKgYen: 0,
    maxWeightKg: 68.0,
    description: 'Envio expresso aéreo. O cálculo considera o maior valor entre o peso real e o peso cubado (C x L x A / 5000).',
    requiresQuote: false
  },
  {
    id: 'fedex_economy',
    type: 'fedex',
    name: 'FedEx International Economy',
    estimatedDays: '4 - 7 dias úteis',
    basePriceYen: 0,
    pricePerKgYen: 0,
    maxWeightKg: 68.0,
    description: 'Envio econômico aéreo. O cálculo considera o maior valor entre o peso real e o peso cubado (C x L x A / 5000).',
    requiresQuote: false
  },
  {
    id: 'ups',
    type: 'ups',
    name: 'UPS Worldwide Expedited',
    estimatedDays: '3 - 6 dias úteis',
    basePriceYen: 0,
    pricePerKgYen: 0,
    maxWeightKg: 70.0,
    description: 'Envio por transportadora expressa privada UPS. Cotação sob consulta de acordo com o CEP no Brasil.',
    requiresQuote: true
  },
  {
    id: 'dhl',
    type: 'dhl',
    name: 'DHL Express Worldwide',
    estimatedDays: '2 - 5 dias úteis',
    basePriceYen: 0,
    pricePerKgYen: 0,
    maxWeightKg: 70.0,
    description: 'Envio por transportadora expressa privada DHL. Cotação sob consulta de acordo com o CEP no Brasil.',
    requiresQuote: true
  }
];

export interface ShippingCostCalculation {
  method: ShippingMethodOption;
  costJpy: number;
  costUsd: number;
  redirectionFeeUsd: number;
  redirectionFeeBrl: number;
  redirectionFeeJpy: number;
  baseCostBrl: number;
  internationalFeeBrl: number; // 7% fee
  totalCostBrl: number;
  isEligible: boolean;
  ineligibilityReason?: string;
  requiresQuote?: boolean;
}

/**
 * Calculates redirection fee for Pro plan based on box weight and monthly box count
 */
export function calcularTaxaRedirecionamento(pesoKg: number, quantidadeCaixasNoMes: number): number {
  // 1. Determina o valor base pela tabela de peso
  let valorBase = 0;
  if (pesoKg <= 1.0) {
    valorBase = 15;
  } else if (pesoKg <= 2.0) {
    valorBase = 20;
  } else {
    // Acima de 2kg / 3kg
    valorBase = 30;
  }

  // 2. Aplica a regra da cota mensal (1ª à 5ª é GRÁTIS)
  if (quantidadeCaixasNoMes <= 5) {
    return 0; // Isento
  }

  // 3. Aplica 30% de desconto da 6ª caixa em diante
  const desconto = 0.30;
  const valorComDesconto = valorBase * (1 - desconto);

  return parseFloat(valorComDesconto.toFixed(2)); // Retorna 10.50, 14.00 ou 21.00
}

/**
 * Calculates service/redirection fee per box based on weight in Kg, monthly box count, and plan
 * - Caixas de até 1kg: $15.00 USD
 * - Caixas de até 2kg: $20.00 USD
 * - Caixa acima de 2kg: $30.00 USD
 */
export function getRedirectionFeeUsd(
  weightKg: number, 
  boxCountInMonth: number = 1, 
  plan: 'free' | 'pro' | 'business' = 'free'
): number {
  const safeWeight = Math.max(0.1, weightKg || 0);

  if (plan === 'business') {
    return 0; // Business: sem taxa de redirecionamento / sem limite
  }

  if (plan === 'pro') {
    return calcularTaxaRedirecionamento(safeWeight, boxCountInMonth);
  }

  if (safeWeight <= 1.0) {
    return 15.00;
  } else if (safeWeight <= 2.0) {
    return 20.00;
  } else {
    return 30.00;
  }
}

/**
 * Calculates international shipping cost for a given weight in Kg and destination country
 */
export function calculateShippingCosts(
  weightKg: number, 
  boxCountInMonth: number = 1, 
  plan: 'free' | 'pro' | 'business' = 'free',
  destinationCountryName: string = 'Brasil',
  isFreeShipping: boolean = false,
  lengthCm: number = 0,
  widthCm: number = 0,
  heightCm: number = 0
): ShippingCostCalculation[] {
  const country = getCountryInfo(destinationCountryName);
  const multiplier = country.shippingMultiplier ?? 1.0;
  const isDomestic = country.isDomesticJapan ?? false;

  const safeWeight = Math.max(0.1, weightKg);
  const redirectionFeeUsd = getRedirectionFeeUsd(safeWeight, boxCountInMonth, plan);
  const redirectionFeeBrl = parseFloat((redirectionFeeUsd * DEFAULT_CURRENCY_RATES.usdToBrl).toFixed(2));
  const redirectionFeeJpy = Math.round(redirectionFeeUsd / DEFAULT_CURRENCY_RATES.jpyToUsd);

  return SHIPPING_METHODS.map((method) => {
    let isEligible = true;
    let ineligibilityReason = '';

    // Handlers for dynamic chargeable weight
    let chargeableWeight = safeWeight;
    if (method.type === 'fedex' && lengthCm > 0 && widthCm > 0 && heightCm > 0) {
      const volumeWeight = (lengthCm * widthCm * heightCm) / 5000;
      chargeableWeight = Math.max(safeWeight, volumeWeight);
    }

    // Rule: Free shipping items up to 2.0 kg must use Small Packet exclusively
    if (isFreeShipping && chargeableWeight <= 2.0 && method.id !== 'small_packet') {
      isEligible = false;
      ineligibilityReason = 'Produtos de Frete Grátis com até 2,0 kg utilizam exclusivamente a modalidade Small Packet.';
    }

    // Handle country-specific availability for Surface and SAL
    if (method.id === 'surface' || method.id === 'sal') {
      const isAvailableForCountry = method.id === 'surface' ? country.surfaceAvailable : country.salAvailable;
      if (!isAvailableForCountry) {
        isEligible = false;
        ineligibilityReason = `Modalidade ${method.name} indisponível no momento para envios a ${country.name}.`;
      }
    } else if (method.isTemporarilyUnavailable) {
      isEligible = false;
      ineligibilityReason = method.unavailableReason || `Modalidade indisponível para envios a ${country.name}.`;
    }

    if (chargeableWeight > method.maxWeightKg) {
      isEligible = false;
      ineligibilityReason = `Limite máximo de ${method.maxWeightKg} kg excedido para esta modalidade.`;
    }

    if (method.requiresQuote) {
      return {
        method,
        costJpy: 0,
        costUsd: 0,
        redirectionFeeUsd,
        redirectionFeeBrl,
        redirectionFeeJpy,
        baseCostBrl: 0,
        internationalFeeBrl: 0,
        totalCostBrl: 0,
        isEligible,
        ineligibilityReason,
        requiresQuote: true
      };
    }

    // Japan Post methods have +25% added to freight cost (except domestic Japan)
    let rawCostJpy = (method.basePriceYen + chargeableWeight * method.pricePerKgYen) * multiplier;
    if (method.type === 'fedex') {
      rawCostJpy = getFedexRate(chargeableWeight, method.id === 'fedex_priority' ? 'priority' : 'economy');
    } else if (isDomestic) {
      rawCostJpy = Math.min(rawCostJpy, 1000 + chargeableWeight * 300); // Domestic Sagawa/Yu-Pack rate
    }

    const handlingFeeMultiplier = (isDomestic || method.type === 'fedex' || method.type === 'ups' || method.type === 'dhl') ? 1.0 : 1.25;
    const costJpy = Math.round(rawCostJpy * handlingFeeMultiplier);
    const freightUsd = parseFloat((costJpy * DEFAULT_CURRENCY_RATES.jpyToUsd).toFixed(2));
    const freightBrl = parseFloat((costJpy * DEFAULT_CURRENCY_RATES.jpyToBrl).toFixed(2));
    
    // Base cost in BRL includes freight + redirection service fee
    const baseCostBrl = parseFloat((freightBrl + redirectionFeeBrl).toFixed(2));
    const internationalFeeBrl = parseFloat((baseCostBrl * DEFAULT_CURRENCY_RATES.internationalFeePercent).toFixed(2));
    const totalCostBrl = parseFloat((baseCostBrl + internationalFeeBrl).toFixed(2));

    return {
      method,
      costJpy,
      costUsd: freightUsd,
      redirectionFeeUsd,
      redirectionFeeBrl,
      redirectionFeeJpy,
      baseCostBrl,
      internationalFeeBrl,
      totalCostBrl,
      isEligible,
      ineligibilityReason,
      requiresQuote: false
    };
  });
}

/**
 * Calculates conversion breakdown from JPY or USD to BRL with redirection fee and 7% markup
 */
export function calculatePaymentBreakdown(
  amountJpy: number, 
  weightKg: number = 1.0, 
  boxCountInMonth: number = 1, 
  plan: 'free' | 'pro' | 'business' = 'free',
  isPersonalShopper: boolean = false
) {
  const safeWeight = Math.max(0.1, weightKg || 1.0);
  const redirectionFeeUsd = isPersonalShopper ? 0 : getRedirectionFeeUsd(safeWeight, boxCountInMonth, plan);
  const redirectionFeeBrl = isPersonalShopper ? 0 : parseFloat((redirectionFeeUsd * DEFAULT_CURRENCY_RATES.usdToBrl).toFixed(2));
  const redirectionFeeJpy = isPersonalShopper ? 0 : Math.round(redirectionFeeUsd / DEFAULT_CURRENCY_RATES.jpyToUsd);

  const freightUsd = parseFloat((amountJpy * DEFAULT_CURRENCY_RATES.jpyToUsd).toFixed(2));
  const freightBrl = parseFloat((amountJpy * DEFAULT_CURRENCY_RATES.jpyToBrl).toFixed(2));

  // Base total BRL is freight + redirection fee
  const baseAmountBrl = parseFloat((freightBrl + redirectionFeeBrl).toFixed(2));
  const internationalFeeBrl = parseFloat((baseAmountBrl * DEFAULT_CURRENCY_RATES.internationalFeePercent).toFixed(2));
  const totalAmountBrl = parseFloat((baseAmountBrl + internationalFeeBrl).toFixed(2));

  return {
    amountJpy, // freight or product total in JPY
    amountUsd: freightUsd,
    freightUsd,
    freightBrl,
    redirectionFeeUsd,
    redirectionFeeBrl,
    redirectionFeeJpy,
    weightKg: safeWeight,
    baseAmountBrl,
    internationalFeeBrl, // 7%
    totalAmountBrl
  };
}

/**
 * Generates InfinitePay JSON Payload based on official API / External Checkout specification
 */
export interface InfinitePayItem {
  quantity: number;
  price: number;
  description: string;
}

export interface InfinitePayPayload {
  handle: string;
  items: InfinitePayItem[];
}

export function createInfinitePayPayload({
  handle = 'egamicorp',
  amountBrl,
  description
}: {
  handle?: string;
  amountBrl: number;
  description: string;
}): InfinitePayPayload {
  const cleanHandle = (handle || 'egamicorp').replace(/^@/, '').trim();
  const priceInCents = Math.max(1, Math.round(amountBrl * 100));

  return {
    handle: cleanHandle,
    items: [
      {
        quantity: 1,
        price: priceInCents,
        description
      }
    ]
  };
}

/**
 * Requests InfinitePay Checkout API endpoint /api/infinitepay/checkout returning { url: "https://checkout.infinitepay.io/egamicorp/..." }
 */
export async function requestInfinitePayCheckoutApi(payload: InfinitePayPayload): Promise<{ url: string; payload: InfinitePayPayload }> {
  try {
    const res = await fetch('/api/infinitepay/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.url) {
        return { url: data.url, payload: data.payload || payload };
      }
    }
  } catch (e) {
    console.warn('InfinitePay API request error:', e);
  }

  return {
    url: `https://checkout.infinitepay.io/${payload.handle || 'egamicorp'}/6vUegwyVd7`,
    payload
  };
}

/**
 * Generates official InfinitePay External Checkout link based on app.infinitepay.io/external-checkout specification
 */
export function generateInfinitePayCheckoutUrl({
  handle = 'egamicorp',
  amountBrl,
  orderId,
  description,
  redirectUrl,
  paymentMethod = 'all'
}: {
  handle?: string;
  amountBrl: number;
  orderId: string;
  description?: string;
  redirectUrl?: string;
  paymentMethod?: 'pix' | 'credit_card' | 'all';
}): string {
  const cleanHandle = (handle || 'egamicorp').replace(/^@/, '').trim();
  const amountInCents = Math.max(1, Math.round(amountBrl * 100));
  
  const baseUrl = `https://app.infinitepay.io/pay/${cleanHandle}`;
  const params = new URLSearchParams();
  params.append('amount', amountInCents.toString());
  params.append('order_id', orderId);
  if (description) params.append('description', description);
  
  const defaultRedirect = typeof window !== 'undefined' ? window.location.href : 'https://dojapao.co.jp';
  const cleanRedirect = sanitizePaymentUrl(redirectUrl || defaultRedirect);
  params.append('redirect_url', cleanRedirect);
  
  if (paymentMethod && paymentMethod !== 'all') {
    params.append('payment_method', paymentMethod);
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generates PIX Copia e Cola, PIX Key, InfinitePay Checkout Link and QR Code payload
 */
export function generatePixData(orderId: string, amountBrl: number, handle: string = 'egamicorp') {
  const pixKey = 'financeiro@dojapao.co.jp';
  const formattedAmount = amountBrl.toFixed(2);
  const amountInCents = Math.round(amountBrl * 100);

  // InfinitePay External Checkout URL for PIX
  const infinitePayPixUrl = generateInfinitePayCheckoutUrl({
    handle,
    amountBrl,
    orderId,
    description: `Pagamento PIX Pedido #${orderId}`,
    paymentMethod: 'pix'
  });

  // EMV standard-like simulated PIX Payload string using key financeiro@dojapao.co.jp
  const pixCopiaECola = `00020126580014BR.GOV.BCB.PIX0122${pixKey}520400005303986540${formattedAmount.length}${formattedAmount}5802BR5915Do Japao Log6007Saitama62070503${orderId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10)}63041A2B`;
  
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixCopiaECola)}`;

  return {
    pixKey,
    pixCopiaECola,
    qrCodeUrl,
    infinitePayPixUrl,
    amountInCents
  };
}

/**
 * Calculates InfinityPay installment simulation for 1 to 12 months in BRL
 */
export function calculateInfinityPayInstallments(totalAmountBrl: number) {
  const installments = [];
  for (let i = 1; i <= 12; i++) {
    // Interest free up to 3x, nominal monthly rate after
    const interestRate = i <= 3 ? 0 : 0.012 * (i - 3);
    const totalWithInterest = totalAmountBrl * (1 + interestRate);
    const monthlyAmount = totalWithInterest / i;
    installments.push({
      number: i,
      monthlyAmount: parseFloat(monthlyAmount.toFixed(2)),
      totalAmount: parseFloat(totalWithInterest.toFixed(2)),
      hasInterest: i > 3
    });
  }
  return installments;
}
